import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
    createEmptyAnnouncement,
    DEFAULT_FALLBACK,
    NbaAnnouncement,
    NbaAnnouncementStatus,
    NbaFallbackMessage,
    NbaPublishMode
} from '../models/next-best-action.model';
import { MentorAnnouncementService } from '../services/mentor-announcement.service';
import {
    mapAnnouncementsResponse,
    MappedAnnouncementState,
    toAnnouncementRequestBody,
    toDefaultMessageRequestBody
} from '../utils/mentor-announcement.mapper';

export interface NextBestActionState {
    announcementId: string | null;
    announcementStatus: NbaAnnouncementStatus;
    announcement: NbaAnnouncement;
    fallback: NbaFallbackMessage;
    loading: boolean;
    saving: boolean;
    error: string | null;
    successMessage: string | null;
}

const initialState: NextBestActionState = {
    announcementId: null,
    announcementStatus: 'none',
    announcement: createEmptyAnnouncement('all'),
    fallback: DEFAULT_FALLBACK,
    loading: false,
    saving: false,
    error: null,
    successMessage: null
};

function applyMappedResponse(mapped: MappedAnnouncementState): Partial<NextBestActionState> {
    return {
        announcementId: mapped.id,
        announcement: mapped.announcement,
        announcementStatus: mapped.status,
        ...(mapped.fallback ? { fallback: mapped.fallback } : {})
    };
}

function getSaveSuccessMessage(publishMode: NbaPublishMode, isUpdate: boolean): string {
    if (publishMode === 'schedule') {
        return isUpdate
            ? 'Scheduled announcement updated successfully.'
            : 'Announcement scheduled successfully.';
    }

    return isUpdate ? 'Announcement updated successfully.' : 'Announcement published successfully.';
}

export const NextBestActionStore = signalStore(
    withState(initialState),
    withMethods((store, api = inject(MentorAnnouncementService)) => ({
        clearSuccessMessage() {
            patchState(store, { successMessage: null });
        },

        updateAnnouncement(changes: Partial<NbaAnnouncement>) {
            patchState(store, {
                announcement: { ...store.announcement(), ...changes }
            });
        },

        updateFallback(changes: Partial<NbaFallbackMessage>) {
            patchState(store, {
                fallback: { ...store.fallback(), ...changes }
            });
        },

        loadAnnouncement() {
            patchState(store, { loading: true, error: null, successMessage: null });

            api.getAnnouncements().subscribe({
                next: (response) => {
                    const mapped = mapAnnouncementsResponse(response);
                    patchState(store, mapped
                        ? { ...applyMappedResponse(mapped), loading: false }
                        : { loading: false });
                },
                error: () => {
                    patchState(store, {
                        loading: false,
                        error: 'Unable to load announcement.'
                    });
                }
            });
        },

        saveAnnouncement(publishMode: NbaPublishMode) {
            const announcement = { ...store.announcement(), publishMode };
            const announcementId = store.announcementId();
            const isUpdate = !!announcementId;
            const payload = toAnnouncementRequestBody(announcement, publishMode, announcementId);
            const request$ = announcementId
                ? api.updateAnnouncement(announcementId, payload)
                : api.createAnnouncement(payload);

            patchState(store, { saving: true, error: null, successMessage: null });

            request$.subscribe({
                next: (response) => {
                    const mapped = mapAnnouncementsResponse(response);
                    const nextStatus = publishMode === 'schedule' ? 'scheduled' : 'live';

                    patchState(store, {
                        saving: false,
                        successMessage: getSaveSuccessMessage(publishMode, isUpdate),
                        ...(mapped
                            ? applyMappedResponse(mapped)
                            : {
                                announcement,
                                announcementStatus: nextStatus
                            })
                    });
                },
                error: () => {
                    patchState(store, {
                        saving: false,
                        error: 'Unable to save announcement.'
                    });
                }
            });
        },

        goLiveAnnouncement() {
            const announcementId = store.announcementId();
            const announcement = {
                ...store.announcement(),
                publishMode: 'now' as NbaPublishMode
            };
            const payload = toAnnouncementRequestBody(announcement, 'now', announcementId);
            const request$ = announcementId
                ? api.updateAnnouncement(announcementId, payload)
                : api.createAnnouncement(payload);

            patchState(store, { saving: true, error: null, successMessage: null });

            request$.subscribe({
                next: (response) => {
                    const mapped = mapAnnouncementsResponse(response);

                    patchState(store, {
                        saving: false,
                        successMessage: 'Announcement is now live.',
                        ...(mapped
                            ? applyMappedResponse(mapped)
                            : {
                                announcement,
                                announcementStatus: 'live'
                            })
                    });
                },
                error: () => {
                    patchState(store, {
                        saving: false,
                        error: 'Unable to publish announcement.'
                    });
                }
            });
        },

        removeAnnouncement() {
            const announcementId = store.announcementId();

            if (!announcementId) {
                patchState(store, {
                    announcementId: null,
                    announcementStatus: 'none',
                    announcement: createEmptyAnnouncement('all'),
                    successMessage: 'Announcement removed successfully.'
                });
                return;
            }

            patchState(store, { saving: true, error: null, successMessage: null });

            api.deleteAnnouncement(announcementId).subscribe({
                next: (response) => {
                    const mapped = mapAnnouncementsResponse(response);

                    patchState(store, mapped
                        ? {
                            saving: false,
                            successMessage: 'Announcement removed successfully.',
                            ...applyMappedResponse(mapped)
                        }
                        : {
                            saving: false,
                            successMessage: 'Announcement removed successfully.',
                            announcementId: null,
                            announcementStatus: 'none',
                            announcement: createEmptyAnnouncement('all')
                        });
                },
                error: () => {
                    patchState(store, {
                        saving: false,
                        error: 'Unable to remove announcement.'
                    });
                }
            });
        },

        saveFallback() {
            const payload = toDefaultMessageRequestBody(store.fallback());

            patchState(store, { saving: true, error: null, successMessage: null });

            api.updateDefaultMessage(payload).subscribe({
                next: (response) => {
                    const mapped = mapAnnouncementsResponse(response);

                    patchState(store, {
                        saving: false,
                        successMessage: 'Default message saved successfully.',
                        ...(mapped?.fallback ? { fallback: mapped.fallback } : {})
                    });
                },
                error: () => {
                    patchState(store, {
                        saving: false,
                        error: 'Unable to save default message.'
                    });
                }
            });
        }
    }))
);
